import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class AssessmentService {

  private baseUrl: string;
  private jobRoles$: Observable<Object>;
  private currentAssessment = null;
  private currentAssessmentSubject = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient, private datePipe: DatePipe,
    @Optional() @Inject('BASE_API_URL') baseUrl?: string) {
    this.baseUrl = baseUrl ? baseUrl + '' : '';
  }

  getTemplate(templateId: string): Observable<any> {
    const url = `${this.baseUrl}templates/${templateId}`;
    return this.http.get(url);
  }

  jobRoleList(): Observable<any> {
    // if (!this.jobRoles$) {
    const url = `${this.baseUrl}enterprise/get-job-roles`;
    // this.jobRoles$ = this.http.get(url).pipe(shareReplay(1));
    //}
    return this.http.get(url);
  }

  createRole(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/create-job-role`;
    return this.http.post(url, data);
  }

  /** Create Assessment */
  createAssessment(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/create`;
    return this.http.post(url, data);
  }

  /** update Assessment */
  updateAssessment(data: any, id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.put(url + id, data);
  }

  updateQuestion(data: any, id): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/`;
    return this.http.put(url + id, data);
  }

  /** save Assessment */
  saveAssessment(data: any, id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/soft-update`;
    return this.http.put(url, data);
  }

  deleteAssessmentQtnType(data: any, id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/question-type`;
    return this.http.delete(url, { body: data });
  }

  /** delete Assessment */
  deleteAssessment(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.delete(url + id);
  }

  /** clone Assessment */
  cloneAssessment(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/clone`;
    return this.http.get(url);
  }

  /**Assessment questions type status update */
  questionsTypeInfoUpdate(id, data): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/question-type-info`;
    return this.http.put(url, data);
  }

  /** get candidate List */
  getInviteLink(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.get(url + id + '/get-invite-link');
  }

  /** Invite Candidate */
  inviteCandidate(id, data): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.post(url + id + '/invite-candidates', data, { headers: { 'skip-loader': 'true' } });
  }

  /** Reset Candidate Assessment */
  resetCandidateAssessment(id, data): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.post(url + id + '/reset-candidate', data, { headers: { 'skip-loader': 'true' } });
  }

  /**get Assessment Details */
  getAssessmentDetails(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.get(url + id);
  }

  /** get candidate List */
  getCandidatesList(id, page, size, minScore = 0, maxScore = 0, status = ['COMPLETED', 'NEW', 'IN_PROGRESS'], startDate = null, endDate = null, sortBy?: string, sortDir: 'asc' | 'desc' = 'desc'): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    let extraUrl = `/candidates?page=${page}&limit=${size}&minScore=${minScore}&maxScore=${maxScore}&status=${status}&sort=${sortDir}`;
    const startParam = this.toIsoDate(startDate);
    const endParam = this.toIsoDate(endDate);
    if (startParam) {
      extraUrl = extraUrl + `&start=${startParam}`;
    }
    if (endParam) {
      extraUrl = extraUrl + `&end=${endParam}`;
    }
    if (sortBy) {
      extraUrl = extraUrl + `&sortBy=${encodeURIComponent(sortBy)}`;
    }
    return this.http.get(url + id + extraUrl);
  }


  /** get candidate List */
  downloadCandidates(id, page, size, minScore = 0, maxScore = 0, status = ['COMPLETED', 'NEW', 'IN_PROGRESS'], startDate = null, endDate = null, sortBy?: string, sortDir: 'asc' | 'desc' = 'desc'): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    let extraUrl = `/candidates?page=${page}&limit=${size}&minScore=${minScore}&maxScore=${maxScore}&status=${status}&sort=${sortDir}&download=true`;
    const startParam = this.toIsoDate(startDate);
    const endParam = this.toIsoDate(endDate);
    if (startParam) {
      extraUrl = extraUrl + `&start=${startParam}`;
    }
    if (endParam) {
      extraUrl = extraUrl + `&end=${endParam}`;
    }
    if (sortBy) {
      extraUrl = extraUrl + `&sortBy=${encodeURIComponent(sortBy)}`;
    }
    const httpOptions = {
      responseType: 'blob' as 'json'
    };
    return this.http.get(url + id + extraUrl, httpOptions);
  }


  /** toggle Assessment Status */
  toggleAssessmentStatus(id: string, data: any) {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.put(url + id, data)
  }

  /** get assignment list request */
  getAssignmentList(): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/list`;
    return this.http.get(url).pipe(
      map((response: Array<any>) => {
        response = response.map(element => {
          element['displayDate'] = this.datePipe.transform(element.createdAt, 'dd-MM-yyyy');
          return element;
        });
        return response;
      })
    );
  }

  /** get assessment questions based on assessment ID */
  getAssessmentUserAnsweres(assessmentId, userId): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/`;
    return this.http.get(url + assessmentId + '/answer-details?candidate=' + userId);
  }

  setCurrentAssessment(assessment) {
    this.currentAssessment = assessment;
    this.currentAssessmentSubject.next(this.currentAssessment);
  }

  getCurrentAssessment() {
    return this.currentAssessment;
  }

  /** Observable stream to react to assessment updates across components */
  get currentAssessment$(): Observable<any> {
    return this.currentAssessmentSubject.asObservable();
  }

  /** Clear current assessment data - should be called on logout */
  clearCurrentAssessment(): void {
    this.currentAssessment = null;
  }

  uploadInviteCSV(id, formData): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/upload-candidates`;
    return this.http.post(url, formData, { headers: { 'skip-loader': 'true' } });
  }

  getWelcomeDataForPreviewAssessment(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/get-question-by-index/admin?question=0`;
    return this.http.get(url);
  }

  private toIsoDate(value: any): string {
    if (!value) { return ''; }
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toISOString().slice(0, 10); // YYYY-MM-DD in UTC
  }

  /**==============ChatGpt APIs ========================*/

  /**Generate thread  for context*/
  generateThread(): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/generate-thread`;
    return this.http.post(url, {});
  }

  /**Generate chatgpt questions */
  generateChatGptQuestions(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/generate-questions`;
    return this.http.post(url, data, { headers: { 'skip-loader': 'true' } });
  }

  /**Get Assessment Questions. */
  getAssessmentQuestions(id, skipLoader: boolean = false): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/questions`;
    const options = skipLoader ? { headers: { 'skip-loader': 'true' } } : {};
    return this.http.get(url, options);
  }

  /**Select Question to the Assessment */
  selectOrUndoQuestionToAssessment(questionId: string, data: any) {
    const url = `${this.baseUrl}enterprise/questions/${questionId}/select`;
    return this.http.put(url, data, { headers: { 'skip-loader': 'true' } });
  }

  /**Get Assessment Meta Data */
  getAssessmentMetaData(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/${id}/question-type-info`;
    return this.http.get(url, { headers: { 'skip-loader': 'true' } });
  }

  downloadAnswers(assessmentId: string, candidateId?: string): Observable<Blob> {
    let url = `${this.baseUrl}enterprise/assessments/${assessmentId}/download-answers`;
    if (candidateId) {
      url += `?candidateId=${candidateId}`;
    }
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  deleteJobRole(roleId: string): Observable<any> {
    const url = `${this.baseUrl}enterprise/job-role/${roleId}`;
    return this.http.delete(url);
  }

  downloadQuestionPaper(assessmentId: string): Observable<Blob> {
    const url = `${this.baseUrl}enterprise/assessments/${assessmentId}/download-question-paper`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  /** Add a new topic */
  addTopic(topic: string, context?: { jobRole?: string; experienceRange?: string }, assessmentId?: string): Observable<any> {
    const url = `${this.baseUrl}enterprise/topic/new-topic`;
    const body: any = { topic };
    if (context?.jobRole) body.jobRole = context.jobRole;
    if (context?.experienceRange) body.experienceRange = context.experienceRange;
    if (assessmentId) body.assessmentId = assessmentId;
    return this.http.post(url, body, { headers: { 'skip-loader': 'true' } });
  }

  /** Delete a topic by ID */
  deleteTopic(topicId: string): Observable<any> {
    const url = `${this.baseUrl}enterprise/topic/${topicId}`;
    return this.http.delete(url);
  }

  /** Extract topics from JD (text or file) */
  extractTopicsFromJd(payload: { jobDescription?: string, file?: File, context?: { jobRole?: string; experienceRange?: string } }): Observable<any> {
    const url = `${this.baseUrl}enterprise/topic/extract-from-jd`;
    const formData = new FormData();
    if (payload.jobDescription) {
      formData.append('jobDescription', payload.jobDescription);
    }
    if (payload.file) {
      formData.append('file', payload.file);
    }
    // Forward optional role/experience context if present in the payload
    if (payload.context?.jobRole) {
      formData.append('jobRole', payload.context.jobRole);
    }
    if (payload.context?.experienceRange) {
      formData.append('experienceRange', payload.context.experienceRange);
    }
    return this.http.post(url, formData, { headers: { 'skip-loader': 'true' } });
  }

  /** Add a new subtopic */
  addSubtopic(subtopic: { name: string, topicId: string }): Observable<any> {
    const url = `${this.baseUrl}enterprise/sub-topic`;
    // Ensure the payload matches the backend expectations exactly
    const payload = {
      name: subtopic.name.trim(),
      topicId: subtopic.topicId
    };
    return this.http.post(url, payload, { headers: { 'skip-loader': 'true' } });
  }

  /** Add a new coding subtopic */
  addCodingSubtopic(subtopic: { name: string, languageId: string }): Observable<any> {
    const url = `${this.baseUrl}enterprise/coding-subtopic`;
    // Ensure the payload matches the backend expectations exactly
    const payload = {
      name: subtopic.name.trim(),
      languageId: subtopic.languageId
    };
    return this.http.post(url, payload, { headers: { 'skip-loader': 'true' } });
  }


}
